import { h } from "vue";
import type { ColumnDef } from "@tanstack/vue-table";
import { Tooltip } from "@/components/ui/tooltip";
import { ChevronRight, ChevronDown, ExternalLink } from "lucide-vue-next";
import { getFlagByCountryCode, getCountryName } from "@utils/constants/regions";
import moment from "moment";
import type { HistoTableRow } from "./types";
import { formatNumber, formatCurrency, formatPercent, getMarginColorClass } from "./formatters";

export const columns: ColumnDef<HistoTableRow>[] = [
  {
    id: "expand",
    header: "",
    size: 32,
    cell: ({ row }) => {
      if (!row.original.isMultiCountry) return null;
      return h(
        "div",
        {
          class: "w-full h-full flex items-center justify-center cursor-pointer",
          onClick: (e: Event) => {
            e.stopPropagation();
            row.toggleExpanded();
          },
        },
        [
          h(row.getIsExpanded() ? ChevronDown : ChevronRight, {
            class: "h-3 w-3",
          }),
        ],
      );
    },
  },
  {
    accessorKey: "completedAt",
    header: "Date",
    size: 70,
    cell: ({ row }) => moment(row.original.completedAt).format("MMM YY"),
  },
  {
    accessorKey: "similarity",
    header: "Similarity",
    size: 70,
    cell: ({ row }) => formatPercent(row.original.similarity),
  },
  {
    accessorKey: "countryCode",
    header: "Country",
    size: 50,
    cell: ({ row }) =>
      h(
        Tooltip,
        { content: getCountryName(row.original.countryCode) },
        {
          default: () => h("span", { class: "cursor-default" }, getFlagByCountryCode(row.original.countryCode)),
        },
      ),
  },
  {
    accessorKey: "surveyName",
    header: "Survey",
    size: 220,
    cell: ({ row }) =>
      h("div", { class: "flex flex-col gap-0.5" }, [
        h("div", { class: "flex items-center gap-1.5" }, [
          row.original.surveyType === "external"
            ? h(ExternalLink, { class: "h-3 w-3 text-muted-foreground shrink-0" })
            : null,
          h(
            "span",
            {
              class: "font-medium truncate block max-w-[190px]",
              title: row.original.surveyName,
            },
            row.original.surveyName,
          ),
        ]),
        h(
          "span",
          {
            class: "text-2xs text-muted-foreground truncate block max-w-[200px] font-normal",
            title: row.original.accountName,
          },
          row.original.accountName,
        ),
      ]),
  },
  {
    accessorKey: "studyType",
    header: "Type",
    size: 70,
    cell: ({ row }) =>
      h(
        "span",
        {
          class: `text-xs px-2 py-0.5 rounded ${
            row.original.studyType === "ai_chat" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"
          }`,
        },
        row.original.studyType === "ai_chat" ? "AI Chat" : "Quanti",
      ),
  },
  {
    accessorKey: "targetDescription",
    header: "Target",
    size: 200,
    cell: ({ row }) => {
      const target = row.original.targetDescription || "--";
      return h(
        Tooltip,
        { content: target },
        {
          default: () =>
            h(
              "span",
              {
                class: "line-clamp-2 leading-tight max-w-[190px] whitespace-normal font-normal cursor-default",
              },
              target,
            ),
        },
      );
    },
  },
  {
    accessorKey: "completesValidCapped",
    header: "N",
    size: 55,
    cell: ({ row }) => formatNumber(row.original.completesValidCapped),
  },
  {
    accessorKey: "revenue",
    header: "Revenue",
    size: 70,
    cell: ({ row }) => formatCurrency(row.original.revenue),
  },
  {
    accessorKey: "recruitmentMargin",
    header: "Margin",
    size: 60,
    cell: ({ row }) => {
      const recruitmentMargin = row.original.recruitmentMargin;
      const adsMargin = row.original.adsMargin;
      const hasExternalCosts = recruitmentMargin !== adsMargin && recruitmentMargin !== null && adsMargin !== null;
      const colorClass = getMarginColorClass(recruitmentMargin);

      if (hasExternalCosts) {
        return h(
          Tooltip,
          {},
          {
            default: () => h("span", { class: `cursor-help ${colorClass}` }, `${recruitmentMargin}%*`),
            content: () =>
              h("div", { class: "space-y-1 text-xs" }, [
                h("div", {}, `Recruitment margin: ${recruitmentMargin}%`),
                h("div", {}, `Ads margin: ${adsMargin}%`),
              ]),
          },
        );
      }

      return h("span", { class: colorClass }, formatPercent(recruitmentMargin));
    },
  },
  {
    accessorKey: "cpi",
    header: "CPI",
    size: 55,
    cell: ({ row }) => formatCurrency(row.original.cpi),
  },
  {
    accessorKey: "clientCpi",
    header: "CPI Client",
    size: 70,
    cell: ({ row }) => formatCurrency(row.original.clientCpi),
  },
  {
    accessorKey: "completionRate",
    header: "Comp.",
    size: 55,
    cell: ({ row }) => formatPercent(row.original.completionRate),
  },
  {
    accessorKey: "screenoutRate",
    header: "SO",
    size: 50,
    cell: ({ row }) => formatPercent(row.original.screenoutRate),
  },
  {
    accessorKey: "loi",
    header: "LOI",
    size: 45,
    cell: ({ row }) => formatNumber(row.original.loi),
  },
  {
    accessorKey: "days",
    header: "Days",
    size: 45,
    cell: ({ row }) => formatNumber(row.original.days),
  },
  {
    accessorKey: "cpm",
    header: "CPM",
    size: 55,
    cell: ({ row }) => formatCurrency(row.original.cpm),
  },
  {
    accessorKey: "ctr",
    header: "CTR",
    size: 50,
    cell: ({ row }) => formatPercent(row.original.ctr),
  },
  {
    accessorKey: "cpc",
    header: "CPC",
    size: 50,
    cell: ({ row }) => formatCurrency(row.original.cpc),
  },
  {
    accessorKey: "startRate",
    header: "Start",
    size: 50,
    cell: ({ row }) => formatPercent(row.original.startRate),
  },
  {
    accessorKey: "cps",
    header: "CPS",
    size: 50,
    cell: ({ row }) => formatCurrency(row.original.cps),
  },
];
